import { NAV_HEADER_HEIGHT } from './NavigationHeader'

/**
 * The BOTTOM SCRIM a photograph needs when a chip floats on it.
 *
 * Extracted from `CertificatePage`'s `mediaFrameSx` when VRG-455's pending-sale
 * page became the second surface to float `AuthenticChip` on a product photo
 * (§36: the second hand-roll is a shared component). Only the scrim moved — the
 * `md` block around it is carousel and desktop-layout specific and stayed put.
 *
 * ## Why a scrim at all, and why it is not optional
 *
 * A translucent white puck is legible on a dark photograph and invisible on a
 * light one, and the catalogue contains both. Measured on the pale Cartier shot,
 * the chip's fill (`grey[100]`, opaque) against the photo immediately around it
 * came to **1.41:1** — the pill's edge simply dissolves.
 *
 * Making the chip heavier cannot fix it, and the arithmetic rather than taste
 * says so: the fill is ALREADY fully opaque and the chip is LIGHT, so pushing it
 * toward white against a pale photo (L≈0.54) reaches 1.77:1 at the theoretical
 * limit — every value in that direction fails. Only two levers move the ratio:
 * darken the chip, which breaks the neutral status language and its black label,
 * or darken what sits behind it.
 *
 * ## The values are the TOP scrim's, not new ones
 *
 * Same stops, same `NAV_HEADER_HEIGHT * 2` falloff, flipped `to top`. The header
 * controls sit in effective alpha ~0.18–0.30 and the chip — 16px up from the
 * image's bottom edge — lands in ~0.21–0.30, so the chip gets the treatment the
 * back button already gets, measured rather than assumed. One vignette
 * vocabulary at both ends of the image instead of two tunings that drift.
 *
 * It reads as photographic vignetting rather than as UI, which is why it is a
 * gradient and not a bar, and why the falloff is long: a gradient ending at the
 * chip's line draws its own horizontal edge.
 *
 * Apply to the element that CONTAINS the photograph — it needs
 * `position: relative`, which is included here.
 */
export const photoScrimSx = {
  position: 'relative' as const,
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: NAV_HEADER_HEIGHT * 2,
    background:
      'linear-gradient(to top, rgba(0,0,0,0.34) 0%, rgba(0,0,0,0.16) 45%, rgba(0,0,0,0) 100%)',
    // Never intercepts a tap or a carousel swipe meant for the photograph.
    pointerEvents: 'none',
  },
}
